import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import translate from "@insite/client-framework/Translate";
import { AddressFieldDisplayCollectionModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { CustomerAddressFormStyles } from "@insite/content-library/Components/CustomerAddressForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { ValidationAddressModel } from "@SupplyLand/Services/AddressValidationService";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    inputAddress: ValidationAddressModel;
    address: ValidationAddressModel;
    isModalOpen: boolean;
    forceValidation: boolean;
    addressValidationPromptRequired: boolean;
    onModalClose: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onAcceptModal: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onContinueModal: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    extendedStyles?: AddressValidationModalStyles;
    addressFieldDisplayCollection: AddressFieldDisplayCollectionModel;
    hideSuggestedAddress?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface AddressValidationModalStyles {
    modal?: ModalPresentationProps;
    addressForm?: CustomerAddressFormStyles;
    container?: GridContainerProps;
    addressButtons?: ButtonPresentationProps;
    acceptEnteredAddressButton?: ButtonPresentationProps;
    closeButton?: ButtonPresentationProps;
    acceptSuggestedAddressButtonGridItem?: GridItemProps;
    addressGridItem?: GridItemProps;
    addressDisplayAndFormGridItem?: GridItemProps;
    addressDisplay?: AddressInfoDisplayStyles;
    addressHeadingText?: TypographyPresentationProps;
    addressInfoDisplayStyles?: AddressInfoDisplayStyles;
}

export const addressValidationModalStyles: AddressValidationModalStyles = {
    closeButton: {
        css: css`
            margin-left: 20px;
        `,
    },
    modal: { size: 400 },
    acceptSuggestedAddressButtonGridItem: { width: 12 },
    addressButtons: {
        color: "primary",
        css: css`
            margin-right: 10px;
        `,
    },
    addressGridItem: {
        width: 12,
        css: css`
            flex-wrap: wrap;
            padding-top: 8px;
            padding-bottom: 8px;
        `,
    },
    addressDisplayAndFormGridItem: { width: 12 },
    addressDisplay: {
        wrapper: {
            css: css`
                font-style: normal;
                margin-top: 15px;
                padding: 20px;
                box-shadow: 0px 0px 4px 2px rgb(0 0 0 / 20%);
                width: 100%;
            `,
        },
    },
};

const AddressValidationModal = ({
    inputAddress,
    address,
    isModalOpen,
    forceValidation,
    addressValidationPromptRequired,
    onModalClose,
    onAcceptModal,
    onContinueModal,
    extendedStyles,
    shipToAddressFields,
    addressFieldDisplayCollection,
    hideSuggestedAddress,
}: Props) => {
    if (!shipToAddressFields) {
        return null;
    }

    const [styles] = useState(() => mergeToNew(addressValidationModalStyles, extendedStyles));

    if (!isModalOpen && (inputAddress == null || address == null)) {
        return null;
    }

    if (!addressValidationPromptRequired) {
        const event = {} as React.MouseEvent<HTMLButtonElement, MouseEvent>;
        if (address.city === undefined && address.state === undefined && address.address1 === undefined) {
            onModalClose(event);
        } else {
            onAcceptModal(event);
        }
    }

    return (
        <>
            {addressValidationPromptRequired ? (
                <Modal
                    {...styles.modal}
                    headline={translate("Verify the Address You Entered")}
                    isOpen={isModalOpen}
                    handleClose={onModalClose}
                    data-test-selector="checkoutShipping_createAddressModal"
                >
                    <GridContainer {...styles.container} data-test-selector="customerAddressFormFields">
                        {!hideSuggestedAddress && (
                            <>
                                <GridItem {...styles.addressGridItem}>
                                    <Typography {...styles.addressHeadingText}>
                                        {translate("Suggested Address")}
                                    </Typography>
                                    <AddressInfoDisplay
                                        {...address}
                                        state={address.state}
                                        country={address.country}
                                        extendedStyles={styles.addressDisplay}
                                    />
                                </GridItem>
                                <GridItem {...styles.acceptSuggestedAddressButtonGridItem}>
                                    <Button {...styles.addressButtons} onClick={onAcceptModal}>
                                        {translate("Accept Suggested Address")}
                                    </Button>
                                </GridItem>
                                <hr />
                            </>
                        )}
                        <GridItem {...styles.addressGridItem}>
                            <Typography {...styles.addressHeadingText}>{translate("Entered Address")}</Typography>
                            <AddressInfoDisplay
                                {...inputAddress}
                                state={inputAddress.state}
                                country={inputAddress.country}
                                extendedStyles={styles.addressDisplay}
                            />
                        </GridItem>
                        <GridItem {...styles.acceptSuggestedAddressButtonGridItem}>
                            <Button {...styles.addressButtons} hidden={forceValidation} onClick={onContinueModal}>
                                {translate("Use This Address")}
                            </Button>
                            <Button {...styles.addressButtons} onClick={onModalClose}>
                                {translate("Edit")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </Modal>
            ) : (
                <></>
            )}
        </>
    );
};

export default connect(mapStateToProps)(AddressValidationModal);
